// АНИМ.1: overlay-компонент для боевых анимаций.
//
// Подписан на Animator.current → notify → re-render. Рисует
// один из четырёх типов overlay: reveal / attack ghost / death /
// heroDeath. Один источник правды для всех PvE+PvP анимаций.

import { useEffect, useRef, useState } from 'react';
import type { Animator, OverlayEvent, RectInfo, CardSnapshot } from './Animator';
import { HeroAvatar } from '../components/HeroAvatar';
import { play } from '../audio/sfx';
import styles from './animations.module.css';

interface Props {
  animator: Animator;
}

// Этап 67.2: маппинг overlay-события → звук. Единая точка для PvE и PvP (оба
// проходят через animator.current). 'hit' для attack играется отдельно на стадии
// impact (см. AnimationLayer ниже).
function battleSfxFor(ev: OverlayEvent): void {
  switch (ev.kind) {
    case 'attack':          play('swing'); break;
    case 'death':           play('death'); break;
    case 'heroDeath':       play('heroDeath'); break;
    case 'summon':          play('summon'); break;
    case 'playAction':      play('cast'); break;
    case 'influence':       play('cast'); break;
    case 'heal':            play('heal'); break;
    case 'buff':            play(ev.dAtk < 0 || ev.dArmor < 0 || ev.dHp < 0 ? 'debuff' : 'buff'); break;
    case 'damage':          play('hit'); break;
    case 'cardPlacement':   play('place'); break;
    case 'cardDraw':        play('draw'); break;
    case 'move':            play('move'); break;
    case 'reveal':          play('reveal'); break;
    case 'stackUp':         play('stack'); break;
    case 'keywordFlash':    play('blip'); break;
    case 'phaseBanner':     play('phase'); break;
    case 'heroImmune':      play('shield'); break;
    case 'parachute':       play('parachute'); break;
    case 'stealthActivate': play('stealth'); break;
    case 'raschet':         play('shot'); break;
    case 'redirect':        break; // тихо
  }
}

export function AnimationLayer({ animator }: Props) {
  const [, force] = useState(0);
  useEffect(() => {
    const unsub = animator.subscribe(() => force((x) => x + 1));
    return () => unsub();
  }, [animator]);

  // Звук боя (Этап 67.2): реагируем на появление НОВОГО overlay-события. Один
  // источник для PvE (engine зовёт методы Animator) и PvP (anim-replay ставит
  // current напрямую) — оба проходят через animator.current + notify сюда.
  const ev = animator.current;
  const lastEvRef = useRef<OverlayEvent | null>(null);
  const lastStageRef = useRef<string | null>(null);
  useEffect(() => {
    if (ev !== lastEvRef.current) {
      lastEvRef.current = ev;
      lastStageRef.current = ev && ev.kind === 'attack' ? ev.stage : null;
      if (ev) battleSfxFor(ev);
    } else if (ev && ev.kind === 'attack' && ev.stage !== lastStageRef.current) {
      lastStageRef.current = ev.stage;
      if (ev.stage === 'impact') play('hit'); // момент попадания
    }
  });

  if (!ev) return null;

  return (
    <div className={styles.overlay}>
      {/* ОТЛ.3: жирный красный индикатор в углу чтобы юзер мог увидеть
          ВООБЩЕ доходит ли событие до AnimationLayer. Если светится —
          subscribe работает, проблема в overlay-рендере (DOMRect, CSS).
          Если не светится — subscribe сломан или current не ставится. */}
      <div style={{
        position: 'fixed',
        top: 80, left: 4,
        width: 24, height: 24,
        borderRadius: '50%',
        background: ev.kind === 'attack' ? '#FF3D3D'
          : ev.kind === 'reveal' ? '#3DF0FF'
          : ev.kind === 'death' ? '#B06BFF'
          : ev.kind === 'heroDeath' ? '#FFB13D'
          : ev.kind === 'heroImmune' ? '#FFD700'
          : ev.kind === 'stealthActivate' ? '#FFFFFF'
          : ev.kind === 'playAction' ? '#9CFF3D'
          : ev.kind === 'heal' ? '#7DF07D'
          : ev.kind === 'buff' ? '#FFB13D'
          : ev.kind === 'summon' ? '#FFB13D'
          : ev.kind === 'redirect' ? '#FFE15A'
          : ev.kind === 'damage' ? '#FF6060'
          : ev.kind === 'parachute' ? '#FFD700'
          : ev.kind === 'raschet' ? '#FF3D3D'
          : ev.kind === 'move' ? '#3DF0FF'
          : ev.kind === 'influence' ? '#3DA0FF'
          : ev.kind === 'stackUp' ? (ev.stackKind === 'infected' ? '#7DF07D' : '#B06BFF')
          : ev.kind === 'keywordFlash' ? (ev.mode === 'add' ? '#FFE15A' : '#FF6060')
          : ev.kind === 'phaseBanner' ? '#3DF0FF'
          : ev.kind === 'cardDraw' ? '#7DF07D'
          : ev.kind === 'cardPlacement' ? '#FFE15A'
          : '#888',
        boxShadow: '0 0 12px currentColor',
        zIndex: 99999,
        pointerEvents: 'none',
      }} title={ev.kind} />

      {ev.kind === 'reveal' && <RevealOverlay event={ev} />}
      {ev.kind === 'attack' && <AttackOverlay event={ev} />}
      {ev.kind === 'death' && <DeathOverlay event={ev} />}
      {ev.kind === 'heroDeath' && <HeroDeathOverlay event={ev} />}
      {ev.kind === 'heroImmune' && <HeroImmuneOverlay event={ev} />}
      {ev.kind === 'stealthActivate' && <StealthActivateOverlay event={ev} />}
      {ev.kind === 'playAction' && <PlayActionOverlay event={ev} />}
      {ev.kind === 'heal' && <HealOverlay event={ev} />}
      {ev.kind === 'buff' && <BuffOverlay event={ev} />}
      {ev.kind === 'summon' && <SummonOverlay event={ev} />}
      {ev.kind === 'redirect' && <RedirectOverlay event={ev} />}
      {ev.kind === 'damage' && <DamageOverlay event={ev} />}
      {ev.kind === 'parachute' && <ParachuteOverlay event={ev} />}
      {ev.kind === 'raschet' && <RaschetOverlay event={ev} />}
      {ev.kind === 'move' && <MoveOverlay event={ev} />}
      {ev.kind === 'influence' && <InfluenceOverlay event={ev} />}
      {ev.kind === 'stackUp' && <StackUpOverlay event={ev} />}
      {ev.kind === 'keywordFlash' && <KeywordFlashOverlay event={ev} />}
      {ev.kind === 'phaseBanner' && <PhaseBannerOverlay event={ev} />}
      {ev.kind === 'cardDraw' && <CardDrawOverlay event={ev} />}
      {ev.kind === 'cardPlacement' && <CardPlacementOverlay event={ev} />}
    </div>
  );
}

// ─── CARD DRAW (АНИМ.16) ─────────────────────────────────
// Face-down ghost карты летит из колоды в руку.
function CardDrawOverlay({ event }: { event: Extract<OverlayEvent, { kind: 'cardDraw' }> }) {
  const fR = event.fromRect;
  const tR = event.toRect;
  const dx = (tR.left + tR.width / 2) - (fR.left + fR.width / 2);
  const dy = (tR.top + tR.height / 2) - (fR.top + fR.height / 2);
  const facClass = styles[`fac-${event.faction}`] || '';
  return (
    <div
      className={`${styles.cardDrawGhost} ${facClass}`}
      style={{
        left: fR.left, top: fR.top,
        width: Math.max(40, fR.width), height: Math.max(50, fR.height),
        ['--draw-dx' as any]: `${dx}px`,
        ['--draw-dy' as any]: `${dy}px`,
      }}
    >
      <div className={styles.cardDrawBack}>?</div>
    </div>
  );
}

// ─── CARD PLACEMENT (АНИМ.16) ────────────────────────────
// Ghost карты с именем летит из руки в slot. После — обычный reveal.
function CardPlacementOverlay({ event }: { event: Extract<OverlayEvent, { kind: 'cardPlacement' }> }) {
  const fR = event.fromRect;
  const tR = event.toRect;
  const dx = (tR.left + tR.width / 2) - (fR.left + fR.width / 2);
  const dy = (tR.top + tR.height / 2) - (fR.top + fR.height / 2);
  const facClass = styles[`fac-${event.snap.faction}`] || '';
  return (
    <div
      className={`${styles.cardPlaceGhost} ${facClass}`}
      style={{
        left: fR.left, top: fR.top,
        width: fR.width, height: fR.height,
        ['--place-dx' as any]: `${dx}px`,
        ['--place-dy' as any]: `${dy}px`,
      }}
    >
      <div className={`${styles.ghostBody} ${facClass}`}>
        <div className={styles.ghostName}>{event.snap.name_ru}</div>
        <div className={styles.ghostAtk}>⚔ {event.snap.atk}</div>
      </div>
    </div>
  );
}

// ─── KEYWORD FLASH (АНИМ.15) ─────────────────────────────
// Короткая вспышка иконки keyword'а над юнитом.
// Лейблы локализуем — короткие RU-теги (Молч/Стелс/Провок/Беш/Прб/ИММ/Подавл).
function KeywordFlashOverlay({ event }: { event: Extract<OverlayEvent, { kind: 'keywordFlash' }> }) {
  const r = event.rect;
  const cx = r.left + r.width / 2;
  const cy = r.top + r.height / 2;
  const kwMap: Record<string, { icon: string; label: string }> = {
    silence:           { icon: '🔇', label: 'Молч' },
    stealth:           { icon: '👁',  label: 'Стелс' },
    taunt:             { icon: '🛡',  label: 'Провок' },
    frenzy:            { icon: '⚡',  label: 'Беш' },
    pierce:            { icon: '➤',  label: 'Прб' },
    immune_first_hit:  { icon: '✦',  label: 'ИММ' },
    suppression:       { icon: '⊘',  label: 'Подавл' },
    infected:          { icon: '☣',  label: 'Зараж' },
    chain_of_command:  { icon: '⛓',  label: 'Цепь' },
  };
  const meta = kwMap[event.keyword] || { icon: '◆', label: event.keyword };
  const text = event.mode === 'add'
    ? `+${meta.icon} ${meta.label}`
    : `−${meta.icon} ${meta.label}`;
  const colorCls = event.mode === 'add' ? styles.dmgBuff : styles.dmgDebuff;
  return (
    <>
      <div
        className={event.mode === 'add' ? styles.buffAura : styles.debuffAura}
        style={{ left: r.left, top: r.top, width: r.width, height: r.height }}
      />
      <div className={styles.dmgLayer} style={{ left: cx, top: cy }}>
        <div
          className={`${styles.dmgNum} ${colorCls}`}
          style={{
            ['--dx' as any]: '0px',
            ['--dy' as any]: '-55px',
            ['--rot' as any]: '0deg',
            ['--delay' as any]: '50ms',
          }}
        >
          {text}
        </div>
      </div>
    </>
  );
}

// ─── PHASE BANNER (АНИМ.15) ──────────────────────────────
// Fullscreen splash на 900мс — текст "Фаза 3 — Бой" с fade-in/out.
function PhaseBannerOverlay({ event }: { event: Extract<OverlayEvent, { kind: 'phaseBanner' }> }) {
  return (
    <div className={styles.phaseBanner}>
      <div className={styles.phaseBannerInner}>
        <div className={styles.phaseBannerPhase}>Фаза {event.phase}</div>
        <div className={styles.phaseBannerLabel}>{event.label}</div>
      </div>
    </div>
  );
}

// ─── STACK UP (АНИМ.14) ──────────────────────────────────
// Mod/Infected счётчик прирос. Всплывающая иконка с цифрой.
function StackUpOverlay({ event }: { event: Extract<OverlayEvent, { kind: 'stackUp' }> }) {
  const r = event.rect;
  const cx = r.left + r.width / 2;
  const cy = r.top + r.height / 2;
  const icon = event.stackKind === 'infected' ? '☣' : '⚙';
  const text = `+${icon} ×${event.count}`;
  const colorCls = event.stackKind === 'infected' ? styles.dmgHeal : styles.dmgMod;
  return (
    <div className={styles.dmgLayer} style={{ left: cx, top: cy }}>
      <div
        className={`${styles.dmgNum} ${colorCls}`}
        style={{
          ['--dx' as any]: '0px',
          ['--dy' as any]: '-55px',
          ['--rot' as any]: '0deg',
          ['--delay' as any]: '50ms',
        }}
      >
        {text}
      </div>
    </div>
  );
}

// ─── MOVE (АНИМ.11) ─────────────────────────────────────
// Юнит-ghost летит из старой ячейки в новую (translate animation).
function MoveOverlay({ event }: { event: Extract<OverlayEvent, { kind: 'move' }> }) {
  const fR = event.fromRect;
  const tR = event.toRect;
  const dx = (tR.left + tR.width / 2) - (fR.left + fR.width / 2);
  const dy = (tR.top + tR.height / 2) - (fR.top + fR.height / 2);
  const facClass = styles[`fac-${event.snap.faction}`] || '';
  return (
    <div
      className={styles.moveGhost}
      style={{
        left: fR.left, top: fR.top, width: fR.width, height: fR.height,
        ['--move-dx' as any]: `${dx}px`,
        ['--move-dy' as any]: `${dy}px`,
      }}
    >
      <div className={`${styles.ghostBody} ${facClass}`}>
        <div className={styles.ghostName}>{event.snap.name_ru}</div>
        <div className={styles.ghostAtk}>⚔ {event.snap.atk}</div>
      </div>
    </div>
  );
}

// ─── INFLUENCE (АНИМ.11) ────────────────────────────────
// Деус разыгрывает карту Влияния — синий вариант playAction.
function InfluenceOverlay({ event }: { event: Extract<OverlayEvent, { kind: 'influence' }> }) {
  return (
    <div className={styles.actionBox} style={{ zIndex: 9999 }}>
      <div className={`${styles.actionCard} ${styles.influenceCard}`}>
        <div className={styles.actionLbl}>◇ ВЛИЯНИЕ</div>
        <div className={styles.actionName}>{event.cardName}</div>
      </div>
      {event.targetRect && (
        <div
          className={styles.actionWave + ' ' + styles.influenceWave}
          style={{
            left: event.targetRect.left + event.targetRect.width / 2,
            top: event.targetRect.top + event.targetRect.height / 2,
          }}
        />
      )}
    </div>
  );
}

// ─── RASCHET INTERRUPT ──────────────────────────────────
// АНИМ.9: Расчёт тяжёлого пулемёта стреляет ДО основного боя.
// Пунктирный красный луч от Расчёта к attacker'у + красная вспышка
// на attacker'е.
function RaschetOverlay({ event }: { event: Extract<OverlayEvent, { kind: 'raschet' }> }) {
  const fR = event.fromRect;
  const tR = event.toRect;
  const fC = { x: fR.left + fR.width / 2, y: fR.top + fR.height / 2 };
  const tC = { x: tR.left + tR.width / 2, y: tR.top + tR.height / 2 };
  const dx = tC.x - fC.x;
  const dy = tC.y - fC.y;
  const length = Math.sqrt(dx * dx + dy * dy);
  const angleDeg = (Math.atan2(dy, dx) * 180) / Math.PI;
  return (
    <>
      {/* Пунктирный красный луч */}
      <div
        className={styles.raschetBeam}
        style={{
          left: fC.x,
          top: fC.y,
          width: length,
          transform: `rotate(${angleDeg}deg)`,
          transformOrigin: '0 50%',
        }}
      />
      {/* Импакт-вспышка на attacker'е (цель пулемёта) */}
      <div
        className={styles.raschetImpact}
        style={{ left: tR.left, top: tR.top, width: tR.width, height: tR.height }}
      >
        <div className={`${styles.impactFlash} ${event.toIsHero ? styles.impactFlashLarge : ''}`} />
        <div className={styles.raschetText}>● РАСЧЁТ</div>
      </div>
    </>
  );
}

// ─── HERO IMMUNE ────────────────────────────────────────
function HeroImmuneOverlay({ event }: { event: Extract<OverlayEvent, { kind: 'heroImmune' }> }) {
  const r = event.rect;
  return (
    <div
      className={styles.immuneBox}
      style={{ left: r.left, top: r.top, width: r.width, height: r.height }}
    >
      <div className={styles.immuneShield} />
      <div className={styles.immuneText}>🛡 IMMUNE</div>
    </div>
  );
}

// ─── STEALTH ACTIVATE ───────────────────────────────────
function StealthActivateOverlay({ event }: { event: Extract<OverlayEvent, { kind: 'stealthActivate' }> }) {
  const r = event.rect;
  return (
    <div
      className={styles.stealthBox}
      style={{ left: r.left, top: r.top, width: r.width, height: r.height }}
    >
      <div className={styles.stealthFlash} />
    </div>
  );
}

// ─── PLAY ACTION ────────────────────────────────────────
function PlayActionOverlay({ event }: { event: Extract<OverlayEvent, { kind: 'playAction' }> }) {
  const facClass = styles[`fac-${event.faction}`] || '';
  // eslint-disable-next-line no-console
  console.log(`[playAction] render: ${event.cardName} (${event.cardId}, ${event.faction})`);
  return (
    <div
      className={styles.actionBox}
      style={{
        // АНИМ.7-debug: высокий z-index для гарантированной видимости
        // поверх любых модалок/Telegram-UI.
        zIndex: 9999,
      }}
    >
      <div className={`${styles.actionCard} ${facClass}`}>
        <div className={styles.actionLbl}>◆ ДЕЙСТВИЕ</div>
        <div className={styles.actionName}>{event.cardName}</div>
      </div>
      {event.targetRect && (
        <div
          className={styles.actionWave}
          style={{
            left: event.targetRect.left + event.targetRect.width / 2,
            top: event.targetRect.top + event.targetRect.height / 2,
          }}
        />
      )}
    </div>
  );
}

// ─── HEAL ───────────────────────────────────────────────
function HealOverlay({ event }: { event: Extract<OverlayEvent, { kind: 'heal' }> }) {
  const r = event.rect;
  const cx = r.left + r.width / 2;
  const cy = r.top + r.height / 2;
  return (
    <>
      <div
        className={styles.healAura}
        style={{ left: r.left, top: r.top, width: r.width, height: r.height }}
      />
      <div className={styles.dmgLayer} style={{ left: cx, top: cy }}>
        <div
          className={`${styles.dmgNum} ${styles.dmgHeal}`}
          style={{
            ['--dx' as any]: '0px',
            ['--dy' as any]: '-50px',
            ['--rot' as any]: '0deg',
            ['--delay' as any]: '50ms',
          }}
        >
          +{event.amount}
        </div>
      </div>
    </>
  );
}

// ─── BUFF / DEBUFF (АНИМ.13) ────────────────────────────
function BuffOverlay({ event }: { event: Extract<OverlayEvent, { kind: 'buff' }> }) {
  const r = event.rect;
  const cx = r.left + r.width / 2;
  const cy = r.top + r.height / 2;
  // АНИМ.13: формат с явным знаком для отрицательных дельт.
  const fmt = (v: number, emoji: string) => v === 0 ? null
    : `${v > 0 ? '+' : ''}${v}${emoji}`;
  const parts: string[] = [];
  const a = fmt(event.dAtk, '⚔'); if (a) parts.push(a);
  const m = fmt(event.dArmor, '🛡'); if (m) parts.push(m);
  const h = fmt(event.dHp, '❤'); if (h) parts.push(h);
  const text = parts.join(' ');
  // Debuff (все < 0) — красный glow вместо жёлтого.
  const isDebuff = event.dAtk < 0 || event.dArmor < 0 || event.dHp < 0;
  const numCls = isDebuff ? styles.dmgDebuff : styles.dmgBuff;
  const auraCls = isDebuff ? styles.debuffAura : styles.buffAura;
  return (
    <>
      <div
        className={auraCls}
        style={{ left: r.left, top: r.top, width: r.width, height: r.height }}
      />
      <div className={styles.dmgLayer} style={{ left: cx, top: cy }}>
        <div
          className={`${styles.dmgNum} ${numCls}`}
          style={{
            ['--dx' as any]: '0px',
            ['--dy' as any]: '-50px',
            ['--rot' as any]: '0deg',
            ['--delay' as any]: '50ms',
          }}
        >
          {text}
        </div>
      </div>
    </>
  );
}

// ─── SUMMON ─────────────────────────────────────────────
function SummonOverlay({ event }: { event: Extract<OverlayEvent, { kind: 'summon' }> }) {
  const r = event.rect;
  return (
    <div
      className={styles.summonBox}
      style={{ left: r.left, top: r.top, width: r.width, height: r.height }}
    >
      <div className={styles.summonBeam} />
      <div className={styles.summonGlow} />
    </div>
  );
}

// ─── PARACHUTE ──────────────────────────────────────────
function ParachuteOverlay({ event }: { event: Extract<OverlayEvent, { kind: 'parachute' }> }) {
  const r = event.rect;
  return (
    <div
      className={styles.parachuteBox}
      style={{ left: r.left, top: r.top, width: r.width, height: r.height }}
    >
      <div className={styles.parachuteIcon}>🪂</div>
      <div className={styles.parachuteText}>HP=1</div>
    </div>
  );
}

// ─── DAMAGE (от Действия) ───────────────────────────────
function DamageOverlay({ event }: { event: Extract<OverlayEvent, { kind: 'damage' }> }) {
  const r = event.rect;
  const cx = r.left + r.width / 2;
  const cy = r.top + r.height / 2;
  return (
    <>
      <div
        className={styles.impactBox}
        style={{ left: r.left, top: r.top, width: r.width, height: r.height }}
      >
        <div className={`${styles.impactFlash} ${event.isHero ? styles.impactFlashLarge : ''}`} />
        <div className={styles.impactShake} />
      </div>
      <div className={styles.dmgLayer} style={{ left: cx, top: cy }}>
        <div
          className={`${styles.dmgNum} ${styles.dmgHp}`}
          style={{
            ['--dx' as any]: '0px',
            ['--dy' as any]: '-50px',
            ['--rot' as any]: '0deg',
            ['--delay' as any]: '50ms',
          }}
        >
          −{event.amount}
        </div>
      </div>
    </>
  );
}

// ─── REDIRECT ───────────────────────────────────────────
function RedirectOverlay({ event }: { event: Extract<OverlayEvent, { kind: 'redirect' }> }) {
  const fR = event.fromRect;
  const tR = event.toRect;
  const fC = { x: fR.left + fR.width / 2, y: fR.top + fR.height / 2 };
  const tC = { x: tR.left + tR.width / 2, y: tR.top + tR.height / 2 };
  const dx = tC.x - fC.x;
  const dy = tC.y - fC.y;
  const length = Math.sqrt(dx * dx + dy * dy);
  const angleDeg = (Math.atan2(dy, dx) * 180) / Math.PI;
  return (
    <div
      className={styles.redirectArrow}
      style={{
        left: fC.x,
        top: fC.y,
        width: length,
        transform: `rotate(${angleDeg}deg)`,
        transformOrigin: '0 50%',
      }}
    >
      <div className={styles.redirectLine} />
      <div className={styles.redirectHead} />
    </div>
  );
}

// ─── REVEAL ──────────────────────────────────────────────
function RevealOverlay({ event }: { event: Extract<OverlayEvent, { kind: 'reveal' }> }) {
  const r = event.rect;
  return (
    <div
      className={styles.revealFlash}
      style={{ left: r.left, top: r.top, width: r.width, height: r.height }}
    />
  );
}

// ─── ATTACK ──────────────────────────────────────────────
function AttackOverlay({ event }: { event: Extract<OverlayEvent, { kind: 'attack' }> }) {
  const aR = event.attackerRect;
  const tR = event.targetRect;
  const aC = { x: aR.left + aR.width / 2, y: aR.top + aR.height / 2 };
  const tC = { x: tR.left + tR.width / 2, y: tR.top + tR.height / 2 };
  const dx = tC.x - aC.x;
  const dy = tC.y - aC.y;

  // Stage → CSS-класс. impact/return ghost остаётся на месте.
  let stageClass = '';
  switch (event.stage) {
    case 'telegraph': stageClass = styles.atkStageTelegraph; break;
    case 'lunge':     stageClass = styles.atkStageLunge; break;
    case 'impact':    stageClass = styles.atkStageImpact; break;
    case 'return':    stageClass = styles.atkStageReturn; break;
  }

  const facClass = styles[`fac-${event.attacker.faction}`] || '';
  const isImpact = event.stage === 'impact' || event.stage === 'return';

  // Damage numbers — показываем только на impact/return.
  const targetNums = isImpact ? computeDamageNumbers(event, 'target') : [];
  const attackerNums = isImpact ? computeDamageNumbers(event, 'attacker') : [];

  return (
    <>
      {/* Ghost атакующего */}
      <div
        className={`${styles.atkGhost} ${stageClass}`}
        style={{
          left: aR.left, top: aR.top, width: aR.width, height: aR.height,
          ['--lunge-dx' as any]: `${dx}px`,
          ['--lunge-dy' as any]: `${dy}px`,
        }}
      >
        <div className={styles.atkGhostGlow} />
        {event.stage === 'lunge' && (
          <>
            <div className={styles.atkTrail + ' ' + styles.atkTrail1} />
            <div className={styles.atkTrail + ' ' + styles.atkTrail2} />
          </>
        )}
        {event.attacker.isHero ? (
          <div className={`${styles.ghostHero} ${facClass}`}>
            <HeroAvatar
              id={event.attacker.id}
              size={Math.min(aR.width, aR.height) - 8}
              faction={event.attacker.faction as any}
              shape="square"
              eager
            />
          </div>
        ) : (
          <div className={`${styles.ghostBody} ${facClass}`}>
            <div className={styles.ghostName}>{event.attacker.name_ru}</div>
            <div className={styles.ghostAtk}>⚔ {event.attacker.atk}</div>
          </div>
        )}
      </div>

      {/* Impact box на target */}
      {isImpact && (
        <div
          className={styles.impactBox}
          style={{ left: tR.left, top: tR.top, width: tR.width, height: tR.height }}
        >
          <div className={`${styles.impactFlash} ${event.target.isHero ? styles.impactFlashLarge : ''}`} />
          <div className={styles.impactShake} />
        </div>
      )}

      {/* Damage numbers — target */}
      {targetNums.length > 0 && (
        <div className={styles.dmgLayer} style={{ left: tC.x, top: tC.y }}>
          {targetNums.map((n, i) => <DamageNum key={'t-' + i} num={n} />)}
        </div>
      )}

      {/* Damage numbers — attacker (если был ответный урон) */}
      {attackerNums.length > 0 && (
        <div className={styles.dmgLayer} style={{ left: aC.x, top: aC.y }}>
          {attackerNums.map((n, i) => <DamageNum key={'a-' + i} num={n} />)}
        </div>
      )}
    </>
  );
}

interface DmgNumber {
  kind: 'arm' | 'hp';
  value: number;
  delayMs: number;
  angleDeg: number;
  rotDeg: number;
}

function computeDamageNumbers(
  ev: Extract<OverlayEvent, { kind: 'attack' }>,
  side: 'attacker' | 'target'
): DmgNumber[] {
  const out: DmgNumber[] = [];
  if (ev.atkDmg === null || ev.defDmg === null) return out;

  const dmgRaw = side === 'target' ? ev.atkDmg : ev.defDmg;
  if (dmgRaw <= 0) return out;
  const snap: CardSnapshot = side === 'target' ? ev.target : ev.attacker;

  let left = dmgRaw;
  let baseDelay = side === 'target' ? 50 : 180;
  let idx = 0;
  // Сначала броня, потом hp.
  if (snap.armor > 0 && left > 0) {
    const armHit = Math.min(snap.armor, left);
    out.push({ kind: 'arm', value: armHit, delayMs: baseDelay + 60 * idx, angleDeg: 0, rotDeg: -10 });
    left -= armHit;
    idx++;
  }
  if (left > 0) {
    out.push({ kind: 'hp', value: left, delayMs: baseDelay + 60 * idx, angleDeg: 0, rotDeg: 12 });
  }
  const total = out.length;
  out.forEach((n, i) => {
    n.angleDeg = total > 1 ? -25 + (i / (total - 1)) * 50 : 0;
    n.rotDeg = (i % 2 === 0 ? -1 : 1) * (8 + i * 3);
  });
  return out;
}

function DamageNum({ num }: { num: DmgNumber }) {
  const angleRad = (num.angleDeg * Math.PI) / 180;
  const dx = Math.sin(angleRad) * 50;
  const dy = -Math.cos(angleRad) * 50;
  return (
    <div
      className={`${styles.dmgNum} ${num.kind === 'arm' ? styles.dmgArm : styles.dmgHp}`}
      style={{
        ['--dx' as any]: `${dx}px`,
        ['--dy' as any]: `${dy}px`,
        ['--rot' as any]: `${num.rotDeg}deg`,
        ['--delay' as any]: `${num.delayMs}ms`,
      }}
    >
      −{num.value}
    </div>
  );
}

// ─── DEATH (АНИМ.17: discard fly) ────────────────────────
// Если знаем discardRect — ghost карты летит в discard pile.
// Иначе — старая fade+scale на месте.
function DeathOverlay({ event }: { event: Extract<OverlayEvent, { kind: 'death' }> }) {
  const r = event.rect;
  if (event.discardRect && event.snap) {
    const dx = (event.discardRect.left + event.discardRect.width / 2) - (r.left + r.width / 2);
    const dy = (event.discardRect.top + event.discardRect.height / 2) - (r.top + r.height / 2);
    const facClass = styles[`fac-${event.snap.faction}`] || '';
    return (
      <>
        <div
          className={styles.deathLayer}
          style={{ left: r.left, top: r.top, width: r.width, height: r.height }}
        >
          <div className={styles.deathFlash} />
        </div>
        <div
          className={styles.deathFly}
          style={{
            left: r.left, top: r.top, width: r.width, height: r.height,
            ['--die-dx' as any]: `${dx}px`,
            ['--die-dy' as any]: `${dy}px`,
          }}
        >
          <div className={`${styles.ghostBody} ${facClass}`}>
            <div className={styles.ghostName}>{event.snap.name_ru}</div>
            <div className={styles.ghostAtk}>⚔ {event.snap.atk}</div>
          </div>
        </div>
      </>
    );
  }
  // Fallback: fade на месте (если discard не нашли).
  return (
    <div
      className={styles.deathLayer}
      style={{ left: r.left, top: r.top, width: r.width, height: r.height }}
    >
      <div className={styles.deathFlash} />
      <div className={styles.deathBody} />
    </div>
  );
}

// ─── HERO DEATH ──────────────────────────────────────────
function HeroDeathOverlay({ event }: { event: Extract<OverlayEvent, { kind: 'heroDeath' }> }) {
  const r = event.rect;
  return (
    <div
      className={styles.heroDeathOverlay}
      style={{ left: r.left, top: r.top, width: r.width, height: r.height }}
    >
      <div className={styles.heroDeathShake} />
      <div className={styles.heroDeathRed} />
      <div className={styles.heroDeathCracks} />
    </div>
  );
}

// Удобный helper для других файлов (RectInfo через document).
export function _testRect(_r: RectInfo): void { /* no-op */ }
